import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useMatches } from "react-router";
import type { RouteHandle } from "@/routes/config";
import {
	Plus,
	Settings,
	LogOut,
	Check,
	X,
	FolderPlus,
	Home,
	List,
	Database as DatabaseIcon,
	Scale,
	Code,
	User,
} from "lucide-react";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import type { Judge } from "@/lib/judges";
import type { Code as CodeType } from "@/lib/codes";
import type { Database } from "@/integrations/supabase/types";

type Project = Database["public"]["Tables"]["projects"]["Row"];
type Dataset = Database["public"]["Tables"]["test_data_sets"]["Row"];

interface WorkspaceSidebarProps {
	projects: Project[];
	judges?: Judge[];
	codes?: CodeType[];
	datasets?: Dataset[];
	onNewProject: () => void;
	onNewJudge?: () => void;
	onNewCode?: () => void;
	onNewDataset?: () => void;
	onUpdateProjectTitle: (id: string, title: string) => void;
	onUpdateJudgeTitle?: (id: string, title: string) => void;
	onUpdateCodeTitle?: (id: string, title: string) => void;
	onOpenSettings: () => void;
}

export const WorkspaceSidebar = ({
	projects,
	judges = [],
	codes = [],
	datasets = [],
	onNewProject,
	onNewJudge,
	onNewCode,
	onNewDataset,
	onUpdateProjectTitle,
	onUpdateJudgeTitle,
	onUpdateCodeTitle,
	onOpenSettings,
}: WorkspaceSidebarProps) => {
	const navigate = useNavigate();
	const { signOut, user } = useAuth();
	const { toast } = useToast();
	const location = useLocation();
	const matches = useMatches();
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editTitle, setEditTitle] = useState("");
	const [manualAccordionValue, setManualAccordionValue] = useState<
		string | undefined
	>();

	// Get sidebar section from the last matched route that has one
	const accordionValue =
		manualAccordionValue ??
		(() => {
			// Iterate from the end (most specific route) to start (root)
			for (let i = matches.length - 1; i >= 0; i--) {
				const handle = matches[i].handle as RouteHandle | undefined;
				if (handle?.sidebarSection) {
					return handle.sidebarSection;
				}
			}
			return "projects"; // default fallback
		})();

	// Reset manual override when route changes, so accordion follows the new route
	useEffect(() => {
		setManualAccordionValue(undefined);
	}, [location.pathname]);

	const handleSignOut = async () => {
		const userName = user?.user_metadata?.["name"] || user?.email;
		await signOut();
		toast({
			title: "Signed out",
			description: `Signed out ${userName}`,
		});
		navigate("/auth");
	};

	const handleEditSave = () => {
		if (editingId && editTitle.trim()) {
			const isProject = projects.some((p) => p.id === editingId);
			const isJudge = judges.some((j) => j.id === editingId);
			const isCode = codes.some((c) => c.id === editingId);

			if (isProject) {
				onUpdateProjectTitle(editingId, editTitle.trim());
			} else if (isJudge && onUpdateJudgeTitle) {
				onUpdateJudgeTitle(editingId, editTitle.trim());
			} else if (isCode && onUpdateCodeTitle) {
				onUpdateCodeTitle(editingId, editTitle.trim());
			}
		}
		setEditingId(null);
		setEditTitle("");
	};

	const isHomeActive = location.pathname === "/workspace";
	const buildWorkspacePath = (segment: string, id: string) =>
		`/workspace/${segment}/${id}`;
	const isWorkspacePathActive = (segment: string, id: string) =>
		location.pathname === buildWorkspacePath(segment, id);
	const isDatasetPathActive = (id: string) =>
		location.pathname === `/dataset/${id}`;

	const handleEditCancel = () => {
		setEditingId(null);
		setEditTitle("");
	};

	return (
		<Sidebar className="w-64">
			<SidebarHeader>
				<div className="space-y-4">
					<div className="px-2">
						<h1 className="text-lg font-semibold text-foreground">
							Deepscan LM
						</h1>
					</div>
					<div className="px-2 py-1.5">
						<SidebarMenuButton
							asChild
							isActive={isHomeActive}
							className="w-full justify-start text-base font-semibold h-auto py-2"
						>
							<Link to="/workspace" className="flex items-center gap-2">
								<Home className="h-5 w-5" />
								Home
							</Link>
						</SidebarMenuButton>
					</div>
				</div>
			</SidebarHeader>

			<Separator className="my-2" />

			<SidebarContent>
				<Accordion
					type="single"
					collapsible
					className="w-full"
					value={accordionValue}
					onValueChange={setManualAccordionValue}
				>
					{/* Projects Section */}
					<AccordionItem value="projects">
						<SidebarGroup>
							<div className="flex items-center justify-between px-2 py-1.5">
								<AccordionTrigger className="hover:no-underline flex-1 py-0">
									<SidebarGroupLabel className="cursor-pointer hover:bg-accent/50 flex items-center gap-2 flex-1 text-base font-semibold">
										<FolderPlus className="h-5 w-5" />
										Evals
										<span className="ml-auto text-xs font-normal text-muted-foreground">
											({projects.length})
										</span>
									</SidebarGroupLabel>
								</AccordionTrigger>
								<div className="flex gap-1">
									<Button
										onClick={() => navigate("/evals")}
										size="sm"
										variant="ghost"
										className="h-6 w-6 p-0"
										title="View all evals"
									>
										<List className="h-4 w-4" />
									</Button>
									<Button
										onClick={onNewProject}
										size="sm"
										variant="ghost"
										className="h-6 w-6 p-0"
										title="Create new eval"
									>
										<Plus className="h-4 w-4" />
									</Button>
								</div>
							</div>
							<AccordionContent>
								<SidebarGroupContent>
									<SidebarMenu>
										{projects.map((project) => (
											<SidebarMenuItem key={project.id}>
												<div className="flex items-center w-full group">
													{editingId === project.id ? (
														<div className="flex items-center flex-1 gap-1">
															<Input
																value={editTitle}
																onChange={(e) => setEditTitle(e.target.value)}
																onKeyDown={(e) => {
																	if (e.key === "Enter") handleEditSave();
																	if (e.key === "Escape") handleEditCancel();
																}}
																className="flex-1 h-8"
																autoFocus
															/>
															<Button
																size="sm"
																variant="ghost"
																onClick={handleEditSave}
																className="h-8 w-8 p-0"
															>
																<Check className="h-3 w-3" />
															</Button>
															<Button
																size="sm"
																variant="ghost"
																onClick={handleEditCancel}
																className="h-8 w-8 p-0"
															>
																<X className="h-3 w-3" />
															</Button>
														</div>
													) : (
														<SidebarMenuButton
															asChild
															isActive={isWorkspacePathActive(
																"evals",
																project.id,
															)}
															className="flex-1 justify-start text-sm"
														>
															<Link
																to={buildWorkspacePath("evals", project.id)}
																className="flex w-full items-center truncate"
															>
																<span className="truncate">
																	{project.title}
																</span>
															</Link>
														</SidebarMenuButton>
													)}
												</div>
											</SidebarMenuItem>
										))}
									</SidebarMenu>
								</SidebarGroupContent>
							</AccordionContent>
						</SidebarGroup>
					</AccordionItem>

					{/* Datasets Section */}
					<AccordionItem value="datasets">
						<SidebarGroup>
							<div className="flex items-center justify-between px-2 py-1.5">
								<AccordionTrigger className="hover:no-underline flex-1 py-0">
									<SidebarGroupLabel className="cursor-pointer hover:bg-accent/50 flex items-center gap-2 flex-1 text-base font-semibold">
										<DatabaseIcon className="h-5 w-5" />
										Datasets
										<span className="ml-auto text-xs font-normal text-muted-foreground">
											({datasets.filter((d) => d.type === "primary").length})
										</span>
									</SidebarGroupLabel>
								</AccordionTrigger>
								<div className="flex gap-1">
									<Button
										onClick={() => navigate("/datasets")}
										size="sm"
										variant="ghost"
										className="h-6 w-6 p-0"
										title="View all datasets"
									>
										<List className="h-4 w-4" />
									</Button>
									<Button
										onClick={onNewDataset}
										size="sm"
										variant="ghost"
										className="h-6 w-6 p-0"
										title="Create new dataset"
									>
										<Plus className="h-4 w-4" />
									</Button>
								</div>
							</div>
							<AccordionContent>
								<SidebarGroupContent>
									<SidebarMenu>
										{datasets.filter((d) => d.type === "primary").map((dataset) => (
											<SidebarMenuItem key={dataset.id}>
												<SidebarMenuButton
													asChild
													isActive={isDatasetPathActive(dataset.id)}
													className="flex-1 justify-start text-sm"
												>
													<Link
														to={`/dataset/${dataset.id}`}
														className="flex w-full items-center truncate"
													>
														<span className="truncate">{dataset.name}</span>
													</Link>
												</SidebarMenuButton>
											</SidebarMenuItem>
										))}
									</SidebarMenu>
								</SidebarGroupContent>
							</AccordionContent>
						</SidebarGroup>
					</AccordionItem>

					{/* Agents Section */}
					<AccordionItem value="agents">
						<SidebarGroup>
							<div className="flex items-center justify-between px-2 py-1.5">
								<AccordionTrigger className="hover:no-underline flex-1 py-0">
									<SidebarGroupLabel className="cursor-pointer hover:bg-accent/50 flex items-center gap-2 flex-1 text-base font-semibold">
										<Code className="h-5 w-5" />
										Agents
										<span className="ml-auto text-xs font-normal text-muted-foreground">
											({codes.length})
										</span>
									</SidebarGroupLabel>
								</AccordionTrigger>
								<div className="flex gap-1">
									<Button
										onClick={() => navigate("/codes")}
										size="sm"
										variant="ghost"
										className="h-6 w-6 p-0"
										title="View all agents"
									>
										<List className="h-4 w-4" />
									</Button>
									{onNewCode && (
										<Button
											onClick={onNewCode}
											size="sm"
											variant="ghost"
											className="h-6 w-6 p-0"
											title="Create new agent"
										>
											<Plus className="h-4 w-4" />
										</Button>
									)}
								</div>
							</div>
							<AccordionContent>
								<SidebarGroupContent>
									<SidebarMenu>
										{codes.map((code) => (
											<SidebarMenuItem key={code.id}>
												<div className="flex items-center w-full group">
													{editingId === code.id ? (
														<div className="flex items-center flex-1 gap-1">
															<Input
																value={editTitle}
																onChange={(e) => setEditTitle(e.target.value)}
																onKeyDown={(e) => {
																	if (e.key === "Enter") handleEditSave();
																	if (e.key === "Escape") handleEditCancel();
																}}
																className="flex-1 h-8"
																autoFocus
															/>
															<Button
																size="sm"
																variant="ghost"
																onClick={handleEditSave}
																className="h-8 w-8 p-0"
															>
																<Check className="h-3 w-3" />
															</Button>
															<Button
																size="sm"
																variant="ghost"
																onClick={handleEditCancel}
																className="h-8 w-8 p-0"
															>
																<X className="h-3 w-3" />
															</Button>
														</div>
													) : (
														<SidebarMenuButton
															asChild
															isActive={isWorkspacePathActive(
																"codes",
																code.id,
															)}
															className="flex-1 justify-start text-sm"
														>
															<Link
																to={buildWorkspacePath("codes", code.id)}
																className="flex w-full items-center truncate"
															>
																<span className="truncate">{code.title}</span>
															</Link>
														</SidebarMenuButton>
													)}
												</div>
											</SidebarMenuItem>
										))}
									</SidebarMenu>
								</SidebarGroupContent>
							</AccordionContent>
						</SidebarGroup>
					</AccordionItem>

					{/* Judges Section */}
					<AccordionItem value="judges">
						<SidebarGroup>
							<div className="flex items-center justify-between px-2 py-1.5">
								<AccordionTrigger className="hover:no-underline flex-1 py-0">
									<SidebarGroupLabel className="cursor-pointer hover:bg-accent/50 flex items-center gap-2 flex-1 text-base font-semibold">
										<Scale className="h-5 w-5" />
										Judges
										<span className="ml-auto text-xs font-normal text-muted-foreground">
											({judges.length})
										</span>
									</SidebarGroupLabel>
								</AccordionTrigger>
								<div className="flex gap-1">
									<Button
										onClick={() => navigate("/judges")}
										size="sm"
										variant="ghost"
										className="h-6 w-6 p-0"
										title="View all judges"
									>
										<List className="h-4 w-4" />
									</Button>
									{onNewJudge && (
										<Button
											onClick={onNewJudge}
											size="sm"
											variant="ghost"
											className="h-6 w-6 p-0"
											title="Create new judge"
										>
											<Plus className="h-4 w-4" />
										</Button>
									)}
								</div>
							</div>
							<AccordionContent>
								<SidebarGroupContent>
									<SidebarMenu>
										{judges.map((judge) => (
											<SidebarMenuItem key={judge.id}>
												<div className="flex items-center w-full group">
													{editingId === judge.id ? (
														<div className="flex items-center flex-1 gap-1">
															<Input
																value={editTitle}
																onChange={(e) => setEditTitle(e.target.value)}
																onKeyDown={(e) => {
																	if (e.key === "Enter") handleEditSave();
																	if (e.key === "Escape") handleEditCancel();
																}}
																className="flex-1 h-8"
																autoFocus
															/>
															<Button
																size="sm"
																variant="ghost"
																onClick={handleEditSave}
																className="h-8 w-8 p-0"
															>
																<Check className="h-3 w-3" />
															</Button>
															<Button
																size="sm"
																variant="ghost"
																onClick={handleEditCancel}
																className="h-8 w-8 p-0"
															>
																<X className="h-3 w-3" />
															</Button>
														</div>
													) : (
														<SidebarMenuButton
															asChild
															isActive={isWorkspacePathActive(
																"judges",
																judge.id,
															)}
															className="flex-1 justify-start text-sm"
														>
															<Link
																to={buildWorkspacePath("judges", judge.id)}
																className="flex w-full items-center truncate"
															>
																<span className="truncate">{judge.title}</span>
															</Link>
														</SidebarMenuButton>
													)}
												</div>
											</SidebarMenuItem>
										))}
									</SidebarMenu>
								</SidebarGroupContent>
							</AccordionContent>
						</SidebarGroup>
					</AccordionItem>
				</Accordion>
			</SidebarContent>

			<SidebarFooter>
				<div className="border-t pt-4">
					<div className="flex items-center justify-between px-2 py-1.5">
						<div className="flex items-center gap-2 flex-1 text-base font-semibold">
							<User className="h-5 w-5" />
							<span>Account</span>
						</div>
						<div className="flex gap-1">
							<Button
								variant="ghost"
								size="sm"
								onClick={onOpenSettings}
								className="h-8 w-8 p-0"
								title="Open settings"
								aria-label="Open settings"
							>
								<Settings className="h-4 w-4" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleSignOut}
								className="h-8 w-8 p-0"
								title="Sign out"
								aria-label="Sign out"
							>
								<LogOut className="h-4 w-4" />
							</Button>
						</div>
					</div>
				</div>
			</SidebarFooter>
		</Sidebar>
	);
};
